// Tiny JSON fetch for the lobby and admin pages. Cookies ride along
// (same origin), so a logged-in session applies to every call. Errors
// carry the server's `error` text so pages can show it as is.
export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
  }
}

export async function api<T>(path: string, method = 'GET', body?: unknown): Promise<T> {
  const res = await fetch(path, {
    method,
    credentials: 'same-origin',
    headers: body === undefined ? undefined : { 'content-type': 'application/json' },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  const text = await res.text();
  let json: unknown;
  try {
    json = text ? JSON.parse(text) : null;
  } catch {
    json = null; // not JSON (a proxy error page): the status is the message
  }
  if (!res.ok) {
    const err = typeof json === 'object' && json !== null && 'error' in json ? String(json.error) : `${res.status} ${res.statusText}`;
    throw new ApiError(res.status, err);
  }
  return json as T;
}

// Small DOM helpers for the plain pages (no framework on purpose).
export function el<K extends keyof HTMLElementTagNameMap>(tag: K, attrs: Record<string, string> = {}, ...children: Array<Node | string>): HTMLElementTagNameMap[K] {
  const e = document.createElement(tag);
  for (const k in attrs) {
    if (k === 'class') e.className = attrs[k]!;
    else e.setAttribute(k, attrs[k]!);
  }
  e.append(...children);
  return e;
}

export function clear(node: Element): void {
  while (node.firstChild) node.removeChild(node.firstChild);
}
